import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const utcDate = (v) => new Date(typeof v === 'string' && !v.endsWith('Z') && !v.includes('+') ? `${v}Z` : v);
const fmtTime = (v) => utcDate(v).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDate = (v) => utcDate(v).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const entryHours = (e) => e.clock_out ? Math.max(0, (utcDate(e.clock_out) - utcDate(e.clock_in)) / 3600000) : 0;

const STATUS_CHIPS = {
  open:              { label: '⬜ Open',                bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0' },
  needs_review:      { label: '🟡 Needs Review',        bg: 'rgba(255,200,0,0.15)',   color: '#FACC15' },
  submitted:         { label: '🔵 Submitted',           bg: 'rgba(30,136,229,0.15)',  color: '#60A5FA' },
  returned:          { label: '🔄 Returned',            bg: 'rgba(255,120,80,0.15)',  color: '#FB923C' },
  resubmitted:       { label: '🔵 Resubmitted',         bg: 'rgba(30,136,229,0.15)',  color: '#60A5FA' },
  sup_approved:      { label: '🔵 Supervisor Approved', bg: 'rgba(30,136,229,0.15)',  color: '#60A5FA' },
  payroll_approved:  { label: '🟢 Payroll Approved',    bg: 'rgba(0,200,150,0.15)',   color: '#00C896' },
  locked:            { label: '🔒 Locked',              bg: 'rgba(160,174,192,0.15)', color: '#CBD5E1' },
  paid:              { label: '✅ Paid',                bg: 'rgba(0,200,150,0.15)',   color: '#00C896' },
  payroll_exception: { label: '🟠 Exception',           bg: 'rgba(255,120,0,0.15)',   color: '#FB923C' },
};

const chipFor = (entry) => {
  const s = entry.approval_status;
  if (!s || s === 'open' || s === 'awaiting_review') {
    if (!entry.clock_out) return STATUS_CHIPS.open;
    return entryHours(entry) * 3600 < 60 ? STATUS_CHIPS.open : STATUS_CHIPS.needs_review;
  }
  return STATUS_CHIPS[s] || STATUS_CHIPS.open;
};

export const StatusChip = ({ entry }) => {
  const c = chipFor(entry);
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
      style={{ background: c.bg, color: c.color }}
      data-testid={`status-chip-${entry.id}`}>
      {c.label}
    </span>
  );
};

const AMENDMENT_STYLES = {
  pending:  { label: '⏳ Pending review', bg: 'rgba(255,200,0,0.12)',  color: '#FACC15', border: 'rgba(255,200,0,0.4)' },
  approved: { label: '✅ Approved',       bg: 'rgba(0,200,150,0.12)',  color: '#00C896', border: 'rgba(0,200,150,0.4)' },
  denied:   { label: '❌ Denied',         bg: 'rgba(239,68,68,0.12)',  color: '#F87171', border: 'rgba(239,68,68,0.4)' },
};

const AmendmentCard = ({ amendment }) => {
  const [expanded, setExpanded] = useState(false);
  const s = AMENDMENT_STYLES[amendment.status] || AMENDMENT_STYLES.pending;
  const refDate = amendment.requested_clock_in || amendment.requested_clock_out || amendment.created_at;
  const denied = amendment.status === 'denied';
  return (
    <div
      className="rounded-xl p-4 cursor-pointer"
      style={{ border: `1.5px dashed ${s.border}`, background: 'rgba(15,25,35,0.6)' }}
      onClick={() => denied && setExpanded((x) => !x)}
      data-testid={`amendment-card-${amendment.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm text-white font-semibold">Missing time request · {fmtDate(refDate)}</p>
          <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>
            {amendment.requested_clock_in ? `In ${fmtTime(amendment.requested_clock_in)}` : ''}
            {amendment.requested_clock_in && amendment.requested_clock_out ? ' → ' : ''}
            {amendment.requested_clock_out ? `Out ${fmtTime(amendment.requested_clock_out)}` : ''}
          </p>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
          style={{ background: s.bg, color: s.color }}
          data-testid={`amendment-status-${amendment.id}`}>
          {s.label}{denied && !expanded ? ' — tap for reason' : ''}
        </span>
      </div>
      <p className="text-xs mt-2 italic" style={{ color: '#64748B' }}>"{amendment.reason}"</p>
      {denied && expanded && (
        <p className="text-xs mt-2 rounded-lg px-3 py-2" style={{ background: 'rgba(239,68,68,0.1)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.3)' }}
          data-testid={`amendment-denied-reason-${amendment.id}`}>
          {amendment.reviewer_notes || 'No reason given by the reviewer.'}
        </p>
      )}
    </div>
  );
};

const EntryCard = ({ entry, token, onResubmitted }) => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const isReturned = entry.approval_status === 'returned';
  const excCount = entry.open_exception_count ?? entry.exception_count ?? 0;
  const hrs = entryHours(entry);

  const resubmit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/time/${entry.id}/resubmit`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Timesheet resubmitted', description: 'Your supervisor will review it again.' });
      onResubmitted();
    } catch (e) {
      toast({ title: 'Could not resubmit', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0F1923] border border-[#2D3748] rounded-xl p-4" data-testid={`history-entry-${entry.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-white font-semibold">{fmtDate(entry.clock_in)}</p>
          <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>
            {fmtTime(entry.clock_in)} → {entry.clock_out ? fmtTime(entry.clock_out) : '—'}
            {entry.clock_out && <> · <span className="font-bold text-white">{hrs.toFixed(2)}h</span></>}
            {(entry.area_label || entry.job_number) && (
              <span style={{ color: '#64748B' }}> · {entry.job_number || entry.area_label}</span>
            )}
          </p>
        </div>
        <StatusChip entry={entry} />
      </div>
      {excCount > 0 && (
        <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full font-semibold ${entry.has_unresolved_exceptions ? 'bg-red-500/15 text-red-300' : 'bg-orange-500/15 text-orange-300'}`}
          data-testid={`exception-chip-${entry.id}`}>
          ⚠️ {excCount} exception{excCount === 1 ? '' : 's'}
        </span>
      )}
      {isReturned && (
        <div className="mt-2">
          <button type="button" onClick={() => setExpanded((x) => !x)}
            className="w-full text-left text-xs font-semibold rounded-lg px-3 py-2"
            style={{ background: 'rgba(255,120,80,0.1)', color: '#FB923C', border: '1px solid rgba(255,120,80,0.3)' }}
            data-testid={`returned-toggle-${entry.id}`}>
            ↩️ Returned — tap to view reason and resubmit
          </button>
          {expanded && (
            <div className="mt-2 space-y-2" data-testid={`returned-detail-${entry.id}`}>
              <p className="text-xs rounded-lg px-3 py-2" style={{ background: 'rgba(255,120,80,0.08)', color: '#FDBA74' }}>
                Supervisor: "{entry.returned_reason || 'No reason recorded.'}"
              </p>
              <button type="button" onClick={resubmit} disabled={busy}
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: '#00C896' }}
                data-testid={`resubmit-btn-${entry.id}`}>
                {busy ? 'Resubmitting…' : '✏️ Correct & Resubmit'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const TimeHistory = ({ token, refreshKey = 0 }) => {
  const [entries, setEntries] = useState([]);
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [e, a] = await Promise.all([
        axios.get(`${API}/time/my-entries`, auth),
        axios.get(`${API}/time/amendments`, auth),
      ]);
      setEntries(e.data.entries || []);
      setAmendments(a.data.amendments || []);
      setError(false);
    } catch (err) { setError(true); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load, refreshKey]);

  if (loading) {
    return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  if (error) {
    return (
      <div className="py-8 text-center" data-testid="time-history-error">
        <p className="text-sm mb-3" style={{ color: '#F87171' }}>Couldn't load your time history.</p>
        <button type="button" onClick={() => { setLoading(true); load(); }}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: '#1E88E5' }}
          data-testid="time-history-retry-btn">
          Try again
        </button>
      </div>
    );
  }

  const items = [
    ...entries.map((e) => ({ type: 'entry', date: utcDate(e.clock_in).getTime(), data: e })),
    ...amendments.map((a) => ({
      type: 'amendment',
      date: utcDate(a.requested_clock_in || a.requested_clock_out || a.created_at).getTime(),
      data: a,
    })),
  ].sort((x, y) => y.date - x.date);

  if (items.length === 0) {
    return (
      <p className="text-sm text-center py-8" style={{ color: '#64748B' }} data-testid="time-history-empty">
        No time entries in the last 30 days.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="time-history-list">
      {items.map((it) => it.type === 'entry'
        ? <EntryCard key={`e-${it.data.id}`} entry={it.data} token={token} onResubmitted={load} />
        : <AmendmentCard key={`a-${it.data.id}`} amendment={it.data} />
      )}
    </div>
  );
};

export default TimeHistory;
